import "./EditorCanvas.css";

import {
    FaAlignCenter,
    FaAlignLeft,
    FaAlignRight,
    FaBold,
    FaCopy,
    FaImage,
    FaMinus,
    FaPalette,
    FaPlus,
    FaQrcode,
    FaSignature,
    FaCheck,
    FaTrash
} from "react-icons/fa";

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { isAllowedImageSource } from "../imageSource";

const PAGE_WIDTH = 700;
const PAGE_HEIGHT = 990;
const TOOLBAR_WIDTH = 220;
const TOOLBAR_HEIGHT = 96;
const TOOLBAR_GAP = 10;

const getToolbarPosition = element => {
    const sideSpace = TOOLBAR_WIDTH + TOOLBAR_GAP;
    const rightSpace = PAGE_WIDTH - element.x - element.width;

    if (rightSpace >= sideSpace) {
        return { left: element.x + element.width + TOOLBAR_GAP, top: element.y };
    }
    if (element.x >= sideSpace) {
        return { left: element.x - sideSpace, top: element.y };
    }

    const left = Math.min(
        PAGE_WIDTH - TOOLBAR_WIDTH,
        Math.max(0, element.x + (element.width - TOOLBAR_WIDTH) / 2)
    );
    const belowSpace = PAGE_HEIGHT - element.y - element.height;
    const top = belowSpace >= TOOLBAR_HEIGHT + TOOLBAR_GAP
        ? element.y + element.height + TOOLBAR_GAP
        : Math.max(0, element.y - TOOLBAR_HEIGHT - TOOLBAR_GAP);

    return { left, top };
};


function EditorCanvas({elements, selectedElement, onSelectElement, onAddElement, onUpdateElement, onDeleteElement, onDuplicateElement, onBeginHistoryStep, onCanvasReady, pageStyle, gridVisible, snapToGrid, zoom = 1}) {


    const canvasRef = useRef(null);
    const canvasAreaRef = useRef(null);
    const wrapperRef = useRef(null);
    const [canvasLayout, setCanvasLayout] = useState({
        scale: 1,
        availableWidth: null,
        wrapperPaddingX: 0,
        wrapperPaddingY: 0
    });
    const setCanvasRef = useCallback(node => {
        canvasRef.current = node;
        onCanvasReady?.(node);
    }, [onCanvasReady]);

    useLayoutEffect(() => {
        const canvasArea = canvasAreaRef.current;
        const wrapper = wrapperRef.current;
        if (!canvasArea || !wrapper) return undefined;

        const updateCanvasScale = () => {
            const areaStyle = window.getComputedStyle(canvasArea);
            const wrapperStyle = window.getComputedStyle(wrapper);
            const pixelValue = value => Number.parseFloat(value) || 0;
            const areaPadding = pixelValue(areaStyle.paddingLeft) + pixelValue(areaStyle.paddingRight);
            const wrapperPaddingX = pixelValue(wrapperStyle.paddingLeft) + pixelValue(wrapperStyle.paddingRight);
            const wrapperPaddingY = pixelValue(wrapperStyle.paddingTop) + pixelValue(wrapperStyle.paddingBottom);
            const availableWidth = canvasArea.clientWidth - areaPadding - wrapperPaddingX;
            if (availableWidth <= 0) return;

            const scale = Math.min(1, availableWidth / PAGE_WIDTH);

            setCanvasLayout(current => (
                Math.abs(current.scale - scale) < 0.001
                && current.availableWidth === availableWidth
                && current.wrapperPaddingX === wrapperPaddingX
                && current.wrapperPaddingY === wrapperPaddingY
                    ? current
                    : { scale, availableWidth, wrapperPaddingX, wrapperPaddingY }
            ));
        };

        updateCanvasScale();
        const resizeObserver = typeof ResizeObserver === "undefined"
            ? null
            : new ResizeObserver(updateCanvasScale);
        resizeObserver?.observe(canvasArea);
        window.addEventListener("resize", updateCanvasScale);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateCanvasScale);
        };
    }, [zoom]);

    const gridSize = 20;
    const snap = value => snapToGrid ? Math.round(value / gridSize) * gridSize : value;
    const canvasScale = canvasLayout.scale * zoom;
    const wrapperWidth = canvasLayout.availableWidth === null
        ? null
        : PAGE_WIDTH * canvasScale + canvasLayout.wrapperPaddingX;
    const wrapperHeight = canvasLayout.availableWidth === null
        ? null
        : PAGE_HEIGHT * canvasScale + canvasLayout.wrapperPaddingY;
    const canvasOverflowsHorizontally = canvasLayout.availableWidth !== null
        && PAGE_WIDTH * canvasScale > canvasLayout.availableWidth;
    const selectedItem = elements.find(element => element.id === selectedElement && element.visible !== false);


    /*
    =========================================
    DROP
    =========================================
    */

    const handleDrop = event => {

        event.preventDefault();

        const type = event.dataTransfer.getData("componentType");

        if (!type) return;

        const canvas = canvasRef.current;

        const rect = canvas.getBoundingClientRect();

        const x = snap((event.clientX - rect.left) * (canvas.clientWidth / rect.width));

        const y = snap((event.clientY - rect.top) * (canvas.clientHeight / rect.height));

        onAddElement(
            type,
            {
                x: Math.max(0, Math.min(x, canvas.clientWidth)),
                y: Math.max(0, Math.min(y, canvas.clientHeight))
            }
        );

    };

    const handleDragOver = event => {

        event.preventDefault();

        event.dataTransfer.dropEffect =
            "copy";

    };

    /*
    =========================================
    MOVER ELEMENTO
    =========================================
    */

    const handleElementPointerDown = (event, element, resizeDirection = null) => {
        event.stopPropagation();
        event.preventDefault();

        onSelectElement(element.id);

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const startX = event.clientX;
        const startY = event.clientY;
        let historyStarted = false;

        const handleMouseMove = e => {
            const deltaX = (e.clientX - startX) * (canvas.clientWidth / (rect.width || canvas.clientWidth || 1));
            const deltaY = (e.clientY - startY) * (canvas.clientHeight / (rect.height || canvas.clientHeight || 1));
            let changes;

            if (resizeDirection) {
                const minimumSize = element.type === "line" ? 2 : 16;
                let left = element.x;
                let top = element.y;
                let right = element.x + element.width;
                let bottom = element.y + element.height;

                if (resizeDirection.includes("w")) left = Math.max(0, Math.min(snap(element.x + deltaX), right - minimumSize));
                if (resizeDirection.includes("e")) right = Math.min(canvas.clientWidth, Math.max(left + minimumSize, snap(element.x + element.width + deltaX)));
                if (resizeDirection.includes("n")) top = Math.max(0, Math.min(snap(element.y + deltaY), bottom - minimumSize));
                if (resizeDirection.includes("s")) bottom = Math.min(canvas.clientHeight, Math.max(top + minimumSize, snap(element.y + element.height + deltaY)));

                changes = { x: left, y: top, width: right - left, height: bottom - top };
            } else {
                changes = {
                    x: Math.max(0, Math.min(snap(element.x + deltaX), canvas.clientWidth - element.width)),
                    y: Math.max(0, Math.min(snap(element.y + deltaY), canvas.clientHeight - element.height))
                };
            }

            if (Object.entries(changes).every(([key, value]) => element[key] === value)) return;
            if (!historyStarted) {
                onBeginHistoryStep();
                historyStarted = true;
            }

            onUpdateElement(element.id, changes, false);
            e.preventDefault();

        };


        const handleMouseUp = () => {

            document.removeEventListener("pointermove", handleMouseMove);

            document.removeEventListener("pointerup", handleMouseUp);

            document.removeEventListener("pointercancel", handleMouseUp);

        };


        document.addEventListener("pointermove", handleMouseMove);

        document.addEventListener("pointerup", handleMouseUp, { once: true });

        document.addEventListener("pointercancel", handleMouseUp, { once: true });

    };


    /*
    =========================================
    RENDER ELEMENT
    =========================================
    */

    const renderShape = element => (
        <div
            className={`canvas-shape canvas-shape-${element.type}`}
            style={{
                backgroundColor: element.fillColor,
                borderColor: element.borderColor,
                borderWidth: element.borderEnabled === false ? 0 : element.borderWidth,
                borderStyle: "solid",
                borderRadius: element.type === "ellipse" ? "50%" : 0
            }}
            data-testid={`canvas-shape-${element.type}`}
        />
    );

    const renderElement = element => {


        switch (element.type) {

            case "text":

                return (

                    <span
                        className="canvas-text"
                        style={{
                            display: "block",
                            width: "100%",
                            textAlign: element.textAlign || "left",
                            whiteSpace: "pre-wrap"
                        }}
                    >
                        {element.text ||
                            "Nuevo texto"}

                    </span>

                );


            case "dynamic":

                return (

                    <span
                        className="canvas-text"
                        style={{
                            display: "block",
                            width: "100%",
                            textAlign: element.textAlign || "left",
                            whiteSpace: "pre-wrap"
                        }}
                    >

                        {`{{${element.variable || "campo"}}}`}

                    </span>

                );


            case "image":
                if (!element.imageSrc || !isAllowedImageSource(element.imageSrc) || element.imageLoadError) {
                    return (
                    <div className="element-placeholder">
                        <FaImage />
                        <span>
                            {element.imageLoadError ? "No se pudo cargar" : "Añade una URL o foto"}
                        </span>
                    </div>
                );
                }

                return (
                    <img
                        className="certificate-image"
                        src={element.imageSrc}
                        alt={element.imageAlt || "Imagen del certificado"}
                        onError={() => onUpdateElement(element.id, { imageLoadError: true }, false)}
                    />
                );


            case "signature":

                return (

                    <div className="element-placeholder">

                        <FaSignature />

                        <span>
                            Firma
                        </span>

                    </div>

                );


            case "qr":

                return (

                    <div className="element-placeholder">

                        <FaQrcode />

                        <span>
                            Código QR
                        </span>

                    </div>

                );


            case "line":

                return null;


            case "rectangle":
                return renderShape(element);

            case "ellipse":
                return renderShape(element);

            case "checkbox":
                return (
                    <div className="certificate-checkbox">
                        <span
                            className={`certificate-checkbox-mark ${element.checked ? "is-checked" : ""}`}
                            data-testid="certificate-checkbox-mark"
                            style={element.checked ? { backgroundColor: element.color, borderColor: element.color } : undefined}
                        >
                            {element.checked && <FaCheck aria-hidden="true" />}
                        </span>
                        <span>{element.text || "Opción verificada"}</span>
                    </div>
                    );


            default:

                return null;

        }

    };

    const handleCanvasClick = (event) => {

        if (event.target === canvasRef.current) {

            onSelectElement(null);

        }

    };


    return (

        <main
            className={`editor-canvas-area${canvasOverflowsHorizontally ? " editor-canvas-area-overflow" : ""}`}
            ref={canvasAreaRef}
        >


            <div
                className="canvas-wrapper"
                ref={wrapperRef}
                style={{
                    width: wrapperWidth === null ? undefined : `${wrapperWidth}px`,
                    height: wrapperHeight === null ? undefined : `${wrapperHeight}px`
                }}
            >


                <div
                    ref={setCanvasRef}
                    className={`certificate-canvas canvas-texture-${pageStyle.texture}`}
                    role="region"
                    aria-label="Hoja del certificado"
                    data-certificate-sheet="true"
                    data-grid-visible={gridVisible}
                    data-snap-to-grid={snapToGrid}
                    style={{
                        "--canvas-scale": canvasScale,
                        "--canvas-inverse-scale": 1 / canvasScale,
                        backgroundColor: pageStyle.backgroundColor
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleCanvasClick}
                >

                    {gridVisible && <div className="canvas-grid-overlay" aria-hidden="true" />}


                    {elements.length === 0 && (

                        <div className="canvas-empty">

                            <div>

                                <strong>
                                    Certificado vacío
                                </strong>

                                <span>
                                    Agrega un componente desde su panel para comenzar a diseñar.
                                </span>

                            </div>

                        </div>

                    )}


                    {elements.map((element, layerIndex) => (

                            <div key={element.id}
                                className={`canvas-element ${selectedElement === element.id ? "selected" : ""}`}
                                data-testid={`canvas-element-${element.id}`}
                                data-layer-index={layerIndex}
                             style={{

                                 left: element.x,

                                 top: element.y,

                                 width: element.width,

                                 height:
                                 element.height,

                                 fontSize:
                                 element.fontSize,

                                 fontWeight:
                                 element.fontWeight,

                                 textAlign:
                                 element.textAlign,

                                 fontStyle:
                                 element.fontStyle,

                                 fontFamily:
                                 element.fontFamily || "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

                                 lineHeight:
                                 1.5,

                                 color:
                                 element.color,

                                 zIndex:
                                 layerIndex + 1,

                                 background:
                                 element.background,

                                 padding:
                                 element.type === "line" || element.type === "rectangle" || element.type === "ellipse" ? 0 : 8,

                                 borderRadius:
                                 element.type === "ellipse" ? "50%" : undefined,

                                 display:
                                 element.visible === false ? "none" : undefined,

                             }}

                             onPointerDown={event =>
                                 handleElementPointerDown(event, element)
                             }

                        >

                            {selectedElement === element.id && ["nw", "n", "ne", "e", "se", "s", "sw", "w"].map(direction => (
                                <button
                                    key={direction}
                                    type="button"
                                    className={`resize-handle resize-handle-${direction}`}
                                    aria-label={`Redimensionar ${element.type} ${direction}`}
                                    onPointerDown={event => handleElementPointerDown(event, element, direction)}
                                />
                            ))}

                            {element.type === "line" ? (

                                <div className="canvas-line" style={{ height: element.height, backgroundColor: element.color }}/>

                            ) : (

                                renderElement(element)
                            )}

                        </div>

                    ))}

                    {selectedItem && (
                        <div
                            className="element-toolbar"
                            role="toolbar"
                            aria-label={`Acciones para ${selectedItem.type}`}
                            style={getToolbarPosition(selectedItem)}
                            onPointerDown={event => event.stopPropagation()}
                            onClick={event => event.stopPropagation()}
                        >
                            <button type="button" aria-label="Duplicar elemento" title="Duplicar" onClick={() => onDuplicateElement(selectedItem.id)}>
                                <FaCopy />
                            </button>
                            <button type="button" aria-label="Eliminar elemento" title="Eliminar" onClick={() => onDeleteElement(selectedItem.id)}>
                                <FaTrash />
                            </button>
                            {(selectedItem.type === "text" || selectedItem.type === "dynamic") && (
                                <>
                                    <span className="element-toolbar-divider" />
                                    {[
                                        ["left", "Alinear a la izquierda", FaAlignLeft],
                                        ["center", "Alinear al centro", FaAlignCenter],
                                        ["right", "Alinear a la derecha", FaAlignRight]
                                    ].map(([alignment, label, Icon]) => (
                                        <button
                                            key={alignment}
                                            type="button"
                                            aria-label={label}
                                            aria-pressed={(selectedItem.textAlign || "left") === alignment}
                                            title={label}
                                            onClick={() => onUpdateElement(selectedItem.id, { textAlign: alignment })}
                                        >
                                            <Icon />
                                        </button>
                                    ))}
                                    <label className="element-toolbar-color" title="Color de texto">
                                        <FaPalette aria-hidden="true" />
                                        <input type="color" aria-label="Color de texto" value={selectedItem.color || "#1F2937"} onChange={event => onUpdateElement(selectedItem.id, { color: event.target.value })} />
                                    </label>
                                    <button type="button" aria-label="Negrita" aria-pressed={selectedItem.fontWeight === "bold"} title="Negrita" onClick={() => onUpdateElement(selectedItem.id, { fontWeight: selectedItem.fontWeight === "bold" ? "normal" : "bold" })}>
                                        <FaBold />
                                    </button>
                                    <div className="element-toolbar-font-size">
                                        <button type="button" aria-label="Reducir tamaño de fuente" title="Reducir tamaño" disabled={(selectedItem.fontSize || 16) <= 1} onClick={() => onUpdateElement(selectedItem.id, { fontSize: Math.max(1, (selectedItem.fontSize || 16) - 1) })}>
                                            <FaMinus />
                                        </button>
                                        <span aria-label="Tamaño de fuente">{selectedItem.fontSize || 16}</span>
                                        <button type="button" aria-label="Aumentar tamaño de fuente" title="Aumentar tamaño" disabled={(selectedItem.fontSize || 16) >= 96} onClick={() => onUpdateElement(selectedItem.id, { fontSize: Math.min(96, (selectedItem.fontSize || 16) + 1) })}>
                                            <FaPlus />
                                        </button>
                                    </div>
                                </>
                            )}
                            {selectedItem.type === "checkbox" && (
                                <label className="element-toolbar-color" title="Color de casilla">
                                    <FaPalette aria-hidden="true" />
                                    <input type="color" aria-label="Color de casilla" value={selectedItem.color || "#1F2937"} onChange={event => onUpdateElement(selectedItem.id, { color: event.target.value })} />
                                </label>
                            )}
                            {selectedItem.type === "line" && (
                                <>
                                    <label className="element-toolbar-color" title="Color de línea">
                                        <FaPalette aria-hidden="true" />
                                        <input type="color" aria-label="Color de línea" value={selectedItem.color || "#1F2937"} onChange={event => onUpdateElement(selectedItem.id, { color: event.target.value })} />
                                    </label>
                                    <input className="element-toolbar-number" type="number" aria-label="Grosor de línea" min="1" max="20" value={Math.min(20, Math.max(1, selectedItem.height || 2))} onChange={event => onUpdateElement(selectedItem.id, { height: Math.min(20, Math.max(1, Number(event.target.value) || 1)) })} />
                                </>
                            )}
                            {(selectedItem.type === "rectangle" || selectedItem.type === "ellipse") && (
                                <>
                                    <label className="element-toolbar-color" title="Color de relleno">
                                        <FaPalette aria-hidden="true" />
                                        <input type="color" aria-label="Color de relleno" value={selectedItem.fillColor && selectedItem.fillColor !== "transparent" ? selectedItem.fillColor : "#FFFFFF"} onChange={event => onUpdateElement(selectedItem.id, { fillColor: event.target.value })} />
                                    </label>
                                    <button type="button" aria-label={selectedItem.borderEnabled === false ? "Activar borde" : "Quitar borde"} aria-pressed={selectedItem.borderEnabled !== false} title={selectedItem.borderEnabled === false ? "Activar borde" : "Quitar borde"} onClick={() => onUpdateElement(selectedItem.id, { borderEnabled: selectedItem.borderEnabled === false })}>
                                        B
                                    </button>
                                    {selectedItem.borderEnabled !== false && (
                                        <>
                                            <label className="element-toolbar-color" title="Color de borde">
                                                <FaPalette aria-hidden="true" />
                                                <input type="color" aria-label="Color de borde" value={selectedItem.borderColor || "#315B7D"} onChange={event => onUpdateElement(selectedItem.id, { borderColor: event.target.value })} />
                                            </label>
                                            <input className="element-toolbar-number" type="number" aria-label="Grosor de borde" min="0" max="20" value={Math.min(20, Math.max(0, selectedItem.borderWidth ?? 2))} onChange={event => onUpdateElement(selectedItem.id, { borderWidth: Math.min(20, Math.max(0, Number(event.target.value) || 0)) })} />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                </div>


            </div>


        </main>

    );

}


export default EditorCanvas;
